import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useMarketingContent } from '../hooks/useMarketingContent';
import MarketingChatbot from '../components/MarketingChatbot';
import { 
  Smartphone, 
  QrCode, 
  BarChart3, 
  Zap, 
  Shield, 
  Globe,
  ChevronRight,
  Play,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Trophy,
  Gift,
  Building2,
  Users,
  Heart,
  Star,
  Menu,
  X,
  Loader2,
  Image,
  Palette
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Map icon names to components for dynamic rendering
const ICON_MAP = {
  Zap: Zap,
  Image: Image,
  BarChart: BarChart3,
  BarChart3: BarChart3,
  QrCode: QrCode,
  Palette: Palette,
  Shield: Shield,
  Globe: Globe,
  Sparkles: Sparkles
};

const HomePage = () => {
  const { t } = useTranslation();
  const { content: cmsContent, loading: cmsLoading } = useMarketingContent();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [contactForm, setContactForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    company: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  
  // CAPTCHA state
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaQuestion, setCaptchaQuestion] = useState({ num1: 0, num2: 0, answer: 0 });
  const [formLoadTime] = useState(Date.now());
  const [honeypot, setHoneypot] = useState(''); // Honeypot field - should remain empty
  
  // Generate new CAPTCHA question on mount
  React.useEffect(() => {
    generateCaptcha();
  }, []);
  
  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setCaptchaQuestion({ num1, num2, answer: num1 + num2 });
    setCaptchaAnswer('');
  };

  const handleContactChange = (e) => {
    setContactForm(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    // Anti-spam: Check honeypot field (should be empty)
    if (honeypot) {
      // Silently reject - likely a bot
      toast.success('Thank you for your message!');
      return;
    }
    
    // Anti-spam: Check if form was filled too quickly (less than 3 seconds)
    const timeSpent = Date.now() - formLoadTime;
    if (timeSpent < 3000) {
      toast.error('Please take your time filling out the form');
      return;
    }
    
    // Validate CAPTCHA
    if (parseInt(captchaAnswer) !== captchaQuestion.answer) {
      toast.error('Please solve the math question correctly');
      generateCaptcha();
      return;
    }
    
    // Validate required fields
    if (!contactForm.first_name.trim()) {
      toast.error('Please enter your first name');
      return;
    }
    if (!contactForm.email.trim()) {
      toast.error('Please enter your email');
      return;
    }
    if (!contactForm.message.trim()) {
      toast.error('Please enter a message');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/api/contact`, {
        ...contactForm,
        _captcha_verified: true,
        _time_spent: timeSpent
      });
      toast.success(response.data.message);
      // Reset form
      setContactForm({
        first_name: '',
        last_name: '',
        email: '',
        company: '',
        message: ''
      });
      generateCaptcha();
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send message. Please try again.');
      generateCaptcha();
    } finally {
      setSubmitting(false);
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const stagger = {
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center">
                <Smartphone className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl text-gray-900">ScanIt2Go</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-gray-600 hover:text-gray-900 transition-colors">{t('marketing.nav.features')}</a>
              <a href="#solutions" className="text-gray-600 hover:text-gray-900 transition-colors">{t('marketing.nav.solutions')}</a>
              <a href="#pricing" className="text-gray-600 hover:text-gray-900 transition-colors">{t('marketing.nav.pricing')}</a>
              <Link to="/about" className="text-gray-600 hover:text-gray-900 transition-colors">{t('marketing.nav.about')}</Link>
              <Link to="/faq" className="text-gray-600 hover:text-gray-900 transition-colors">{t('marketing.nav.faq')}</Link>
              <a href="#contact" className="text-gray-600 hover:text-gray-900 transition-colors">{t('marketing.nav.contact')}</a>
            </div>

            {/* CTA Buttons */}
            <div className="hidden md:flex items-center gap-4">
              <Link to="/admin" className="text-gray-600 hover:text-gray-900 transition-colors">
                {t('marketing.nav.login')}
              </Link>
              <a 
                href="#contact"
                className="px-5 py-2.5 bg-indigo-600 text-white rounded-full font-medium hover:bg-indigo-700 transition-colors"
              >
                {t('marketing.nav.getStarted')}
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="md:hidden bg-white border-t border-gray-100"
          >
            <div className="px-6 py-4 space-y-4">
              <a href="#features" className="block text-gray-600 hover:text-gray-900">{t('marketing.nav.features')}</a>
              <a href="#solutions" className="block text-gray-600 hover:text-gray-900">{t('marketing.nav.solutions')}</a>
              <a href="#pricing" className="block text-gray-600 hover:text-gray-900">{t('marketing.nav.pricing')}</a>
              <Link to="/about" className="block text-gray-600 hover:text-gray-900">{t('marketing.nav.about')}</Link>
              <Link to="/faq" className="block text-gray-600 hover:text-gray-900">{t('marketing.nav.faq')}</Link>
              <a href="#contact" className="block text-gray-600 hover:text-gray-900">{t('marketing.nav.contact')}</a>
              <hr className="border-gray-100" />
              <Link to="/admin" className="block text-gray-600 hover:text-gray-900">{t('marketing.nav.login')}</Link>
              <a 
                href="#contact"
                className="block w-full text-center px-5 py-2.5 bg-indigo-600 text-white rounded-full font-medium"
              >
                {t('marketing.nav.getStarted')}
              </a>
            </div>
          </motion.div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={stagger}
            >
              <motion.div 
                variants={fadeInUp}
                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 rounded-full text-indigo-700 text-sm font-medium mb-6"
              >
                <Sparkles className="w-4 h-4" />
                {cmsContent.hero?.badge || t('marketing.hero.badge')}
              </motion.div>
              
              <motion.h1 
                variants={fadeInUp}
                className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6"
              >
                {cmsContent.hero?.title || (
                  <>
                    {t('marketing.hero.title')}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600"> {t('marketing.hero.titleHighlight')}</span>
                    {t('marketing.hero.titleEnd')}
                  </>
                )}
              </motion.h1>
              
              <motion.p 
                variants={fadeInUp}
                className="text-xl text-gray-600 mb-8 leading-relaxed"
              >
                {cmsContent.hero?.subtitle || t('marketing.hero.subtitle')}
              </motion.p>
              
              <motion.div 
                variants={fadeInUp}
                className="flex flex-wrap gap-4"
              >
                <a 
                  href="#contact"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-indigo-600 text-white rounded-full font-semibold hover:bg-indigo-700 transition-all hover:scale-105"
                >
                  {cmsContent.hero?.cta_primary || t('marketing.hero.requestDemo')}
                  <ArrowRight className="w-5 h-5" />
                </a>
                <a 
                  href="#features"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-gray-100 text-gray-900 rounded-full font-semibold hover:bg-gray-200 transition-all"
                >
                  <Play className="w-5 h-5" />
                  {cmsContent.hero?.cta_secondary || t('marketing.hero.seeHowItWorks')}
                </a>
              </motion.div>

              <motion.div 
                variants={fadeInUp}
                className="mt-10 flex items-center gap-8"
              >
                <div className="flex -space-x-3">
                  {[1,2,3,4].map(i => (
                    <div key={i} className={`w-10 h-10 rounded-full border-2 border-white bg-gradient-to-br ${
                      i === 1 ? 'from-blue-400 to-blue-600' :
                      i === 2 ? 'from-green-400 to-green-600' :
                      i === 3 ? 'from-purple-400 to-purple-600' :
                      'from-orange-400 to-orange-600'
                    }`} />
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-1">
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-sm text-gray-600">
                    {cmsContent.hero?.stats?.label || t('marketing.hero.trustedBy')}
                  </p>
                </div>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="relative"
            >
              <div className="relative z-10 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-3xl p-8">
                <div className="bg-white rounded-2xl shadow-2xl p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                      <QrCode className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Product Tag</h3>
                      <p className="text-sm text-gray-500">Tap or scan to engage</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="h-3 bg-gray-100 rounded-full w-full" />
                    <div className="h-3 bg-gray-100 rounded-full w-3/4" />
                    <div className="h-32 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl flex items-center justify-center">
                      <Smartphone className="w-16 h-16 text-indigo-300" />
                    </div>
                    <div className="flex gap-2">
                      <div className="flex-1 h-10 bg-indigo-600 rounded-lg" />
                      <div className="w-10 h-10 bg-gray-100 rounded-lg" />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Floating Elements */}
              <div className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-lg p-4 z-20">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">1.7K+</p>
                    <p className="text-xs text-gray-500">Scans today</p>
                  </div>
                </div>
              </div>
              
              <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-lg p-4 z-20">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <Globe className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Global Reach</p>
                    <p className="text-xs text-gray-500">180+ countries</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Logos Section */}
      <section className="py-12 border-y border-gray-100 bg-gray-50/50">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-center text-sm text-gray-500 mb-8">{t('marketing.trustedBrands')}</p>
          <div className="flex flex-wrap justify-center items-center gap-12 opacity-50">
            {['Brand 1', 'Brand 2', 'Brand 3', 'Brand 4', 'Brand 5'].map((brand, i) => (
              <div key={i} className="text-2xl font-bold text-gray-400">{brand}</div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl font-bold text-gray-900 mb-4"
            >
              {cmsContent.features?.title || t('marketing.features.title')}
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-xl text-gray-600 max-w-2xl mx-auto"
            >
              {cmsContent.features?.subtitle || t('marketing.features.subtitle')}
            </motion.p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Use CMS items if available, otherwise fall back to defaults */}
            {(cmsContent.features?.items?.length > 0 ? cmsContent.features.items : [
              {
                icon: 'QrCode',
                title: t('marketing.features.dualTech'),
                description: t('marketing.features.dualTechDesc'),
                color: 'indigo'
              },
              {
                icon: 'Zap',
                title: t('marketing.features.instantUpdates'),
                description: t('marketing.features.instantUpdatesDesc'),
                color: 'yellow'
              },
              {
                icon: 'BarChart',
                title: t('marketing.features.realTimeAnalytics'),
                description: t('marketing.features.realTimeAnalyticsDesc'),
                color: 'green'
              },
              {
                icon: 'Shield',
                title: t('marketing.features.whiteLabel'),
                description: t('marketing.features.whiteLabelDesc'),
                color: 'purple'
              },
              {
                icon: 'Globe',
                title: t('marketing.features.multiLanguage'),
                description: t('marketing.features.multiLanguageDesc'),
                color: 'blue'
              },
              {
                icon: 'Sparkles',
                title: t('marketing.features.aiPowered'),
                description: t('marketing.features.aiPoweredDesc'),
                color: 'pink'
              }
            ]).map((feature, i) => {
              const IconComponent = ICON_MAP[feature.icon] || Sparkles;
              const colorClass = feature.color || 'indigo';
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="group bg-white rounded-2xl p-8 border border-gray-100 hover:border-indigo-200 hover:shadow-xl transition-all duration-300"
                >
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 bg-${colorClass}-100 text-${colorClass}-600 group-hover:scale-110 transition-transform`}>
                    <IconComponent className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section id="solutions" className="py-24 px-6 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl font-bold text-gray-900 mb-4"
            >
              {t('marketing.solutions.title')}
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-xl text-gray-600"
            >
              {t('marketing.solutions.subtitle')}
            </motion.p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* B2B Platform */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl p-10 border border-gray-100 hover:shadow-2xl transition-all duration-500"
            >
              <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mb-6">
                <Building2 className="w-8 h-8 text-indigo-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('marketing.solutions.forBusiness')}</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                {t('marketing.solutions.forBusinessDesc')}
              </p>
              <ul className="space-y-3 mb-8">
                {(t('marketing.solutions.businessFeatures', { returnObjects: true }) || [
                  'Manage thousands of tags at scale',
                  'Real-time analytics dashboard',
                  'White-label for your brand',
                  'API access & integrations',
                  'Multi-tenant architecture'
                ]).map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-700">
                    <CheckCircle2 className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-sm text-gray-500 mb-4">{t('marketing.solutions.perfectFor')}</p>
              <div className="flex flex-wrap gap-2">
                {(t('marketing.solutions.businessTags', { returnObjects: true }) || ['Marketing Agencies', 'Retail Brands', 'Event Companies', 'Manufacturers', 'Packaged Goods', 'Entertainment', 'Sports']).map((tag, i) => (
                  <span key={i} className="px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full text-sm">
                    {tag}
                  </span>
                ))}
              </div>
            </motion.div>

            {/* Gifts & Awards */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl p-10 border border-gray-100 hover:shadow-2xl transition-all duration-500"
            >
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mb-6">
                <Gift className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('marketing.solutions.giftsAwards')}</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                {t('marketing.solutions.giftsAwardsDesc')}
              </p>
              <ul className="space-y-3 mb-8">
                {(t('marketing.solutions.giftFeatures', { returnObjects: true }) || [
                  'Smart greeting cards with video',
                  'Trophies that replay victory moments',
                  'Wedding favors with memories',
                  'Corporate awards with messages',
                  'Memorial keepsakes'
                ]).map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-700">
                    <CheckCircle2 className="w-5 h-5 text-purple-600 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link 
                to="/gifts" 
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-full font-medium hover:opacity-90 transition-all"
              >
                {t('marketing.solutions.viewProducts')}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl font-bold text-gray-900 mb-4"
            >
              How It Works
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-xl text-gray-600"
            >
              Three simple steps to create interactive experiences
            </motion.p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Create Your Tag',
                description: 'Generate unique NFC tags and QR codes in seconds. Customize colors and branding.',
                icon: <QrCode className="w-8 h-8" />
              },
              {
                step: '02',
                title: 'Add Content',
                description: 'Upload photos, videos, audio and messages. Use AI to help write compelling content.',
                icon: <Sparkles className="w-8 h-8" />
              },
              {
                step: '03',
                title: 'Track & Engage',
                description: 'Monitor scans in real-time. See analytics, locations, and engagement metrics.',
                icon: <BarChart3 className="w-8 h-8" />
              }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="relative"
              >
                <div className="text-8xl font-bold text-gray-100 absolute -top-4 -left-4">{item.step}</div>
                <div className="relative bg-white rounded-2xl p-8 border border-gray-100">
                  <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center text-indigo-600 mb-6">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
                {i < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <ChevronRight className="w-8 h-8 text-gray-300" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 px-6 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl font-bold text-gray-900 mb-4"
            >
              {cmsContent.pricing?.title || 'Simple, Transparent Pricing'}
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-xl text-gray-600"
            >
              {cmsContent.pricing?.subtitle || 'Start free, scale as you grow'}
            </motion.p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Use CMS plans if available, otherwise fallback to defaults */}
            {(cmsContent.pricing?.plans?.length > 0 ? cmsContent.pricing.plans : [
              {
                name: 'Starter',
                price: '$39',
                period: '/month',
                description: 'Perfect for small businesses',
                features: [
                  'Up to 100 tags',
                  'Basic analytics',
                  'QR code generator',
                  'Email support',
                  'Secure encryption'
                ],
                cta: 'Start Free Trial',
                popular: false
              },
              {
                name: 'Professional',
                price: '$99',
                period: '/month',
                description: 'For growing businesses',
                features: [
                  'Up to 1,000 tags',
                  'Advanced analytics',
                  'Custom QR branding',
                  'API access',
                  'Priority support',
                  'Team collaboration',
                  'Secure encryption'
                ],
                cta: 'Start Free Trial',
                popular: true
              },
              {
                name: 'Enterprise',
                price: 'Custom',
                period: '',
                description: 'For large organizations',
                features: [
                  'Unlimited tags',
                  'White-label platform',
                  'Dedicated support',
                  'Custom integrations',
                  'SLA guarantee',
                  'On-boarding assistance',
                  'Secure encryption'
                ],
                cta: 'Contact Sales',
                popular: false
              }
            ]).map((plan, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className={`relative bg-white rounded-3xl p-8 border-2 ${
                  plan.popular ? 'border-indigo-600 shadow-xl' : 'border-gray-100'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="px-4 py-1 bg-indigo-600 text-white text-sm font-medium rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}
                <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                <p className="text-gray-500 text-sm mb-6">{plan.description}</p>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                  <span className="text-gray-500">{plan.period}</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, fi) => (
                    <li key={fi} className="flex items-center gap-3 text-gray-700">
                      <CheckCircle2 className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <a
                  href="#contact"
                  className={`block w-full text-center py-3 rounded-xl font-semibold transition-all ${
                    plan.popular
                      ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  }`}
                >
                  {plan.cta}
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl font-bold text-gray-900 mb-4"
            >
              {cmsContent.testimonials?.title || 'Loved by Businesses'}
            </motion.h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Use CMS testimonials if available, otherwise fallback */}
            {(cmsContent.testimonials?.items?.length > 0 ? cmsContent.testimonials.items : [
              {
                quote: "ScanIt2Go transformed how we engage customers. The analytics alone have been game-changing for our retail strategy.",
                author: "Sarah Johnson",
                role: "Marketing Director",
                company: "Fashion Retail Co"
              },
              {
                quote: "We added NFC tags to our awards. Winners can now relive their victory moment anytime. It's magical.",
                author: "Michael Chen",
                role: "Event Manager",
                company: "Sports Awards Inc"
              },
              {
                quote: "The white-label feature let us offer this to our clients under our brand. Revenue stream we didn't expect!",
                author: "Emma Williams",
                role: "Agency Owner",
                company: "Digital First Agency"
              }
            ]).map((testimonial, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-8 border border-gray-100"
              >
                <div className="flex gap-1 mb-4">
                  {[1,2,3,4,5].map(s => (
                    <Star key={s} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed">"{testimonial.quote}"</p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-purple-400 rounded-full" />
                  <div>
                    <p className="font-semibold text-gray-900">{testimonial.author}</p>
                    <p className="text-sm text-gray-500">{testimonial.role}, {testimonial.company}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-3xl p-12 text-center text-white"
          >
            <h2 className="text-4xl font-bold mb-4">Ready to Get Started?</h2>
            <p className="text-xl text-indigo-100 mb-8">
              Join 500+ businesses creating interactive experiences with ScanIt2Go
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a 
                href="#contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-indigo-600 rounded-full font-semibold hover:bg-gray-100 transition-all"
              >
                Request Demo
                <ArrowRight className="w-5 h-5" />
              </a>
              <a 
                href="#pricing"
                className="inline-flex items-center gap-2 px-8 py-4 bg-indigo-500 text-white rounded-full font-semibold hover:bg-indigo-400 transition-all"
              >
                View Pricing
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {cmsContent.contact?.title || t('marketing.contact.title')}
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                {cmsContent.contact?.subtitle || t('marketing.contact.subtitle')}
              </p>
              
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                    <Globe className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{t('marketing.contact.website')}</p>
                    <a href="https://app.scanit2go.com" className="text-indigo-600">
                      {cmsContent.contact?.website || 'www.scanit2go.com'}
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{t('marketing.contact.email')}</p>
                    <a href={`mailto:${cmsContent.contact?.email || 'andy@scanit2go.com'}`} className="text-indigo-600">
                      {cmsContent.contact?.email || 'andy@scanit2go.com'}
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{t('marketing.contact.phone')}</p>
                    <a href={`tel:${(cmsContent.contact?.phone || '+44 7829 710 543').replace(/\s/g, '')}`} className="text-indigo-600">
                      {cmsContent.contact?.phone || '+44 7829 710 543'}
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <form onSubmit={handleContactSubmit} className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('marketing.contact.firstName')} *</label>
                    <input 
                      type="text"
                      name="first_name"
                      value={contactForm.first_name}
                      onChange={handleContactChange}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                      placeholder="John"
                      data-testid="contact-first-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('marketing.contact.lastName')}</label>
                    <input 
                      type="text"
                      name="last_name"
                      value={contactForm.last_name}
                      onChange={handleContactChange}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                      placeholder="Doe"
                      data-testid="contact-last-name"
                    />
                  </div>
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('marketing.contact.emailLabel')} *</label>
                  <input 
                    type="email"
                    name="email"
                    value={contactForm.email}
                    onChange={handleContactChange}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                    placeholder="john@company.com"
                    data-testid="contact-email"
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('marketing.contact.company')}</label>
                  <input 
                    type="text"
                    name="company"
                    value={contactForm.company}
                    onChange={handleContactChange}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                    placeholder="Your Company"
                    data-testid="contact-company"
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('marketing.contact.message')} *</label>
                  <textarea 
                    rows={4}
                    name="message"
                    value={contactForm.message}
                    onChange={handleContactChange}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all resize-none"
                    placeholder={t('marketing.contact.messagePlaceholder')}
                    data-testid="contact-message"
                  />
                </div>
                
                {/* Honeypot field - hidden from humans, bots will fill it */}
                <div className="absolute -left-[9999px]" aria-hidden="true">
                  <input 
                    type="text"
                    name="website"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>
                
                {/* Simple Math CAPTCHA */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('marketing.contact.captcha') || 'Security Check'}: What is {captchaQuestion.num1} + {captchaQuestion.num2}? *
                  </label>
                  <div className="flex items-center gap-3">
                    <input 
                      type="number"
                      value={captchaAnswer}
                      onChange={(e) => setCaptchaAnswer(e.target.value)}
                      className="w-32 px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                      placeholder="?"
                      data-testid="contact-captcha"
                      required
                    />
                    <button
                      type="button"
                      onClick={generateCaptcha}
                      className="text-sm text-indigo-600 hover:text-indigo-800 transition-colors"
                    >
                      {t('marketing.contact.newQuestion') || 'New question'}
                    </button>
                  </div>
                </div>
                <button 
                  type="submit"
                  disabled={submitting}
                  className="w-full py-4 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="contact-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {t('marketing.contact.sending')}
                    </>
                  ) : (
                    t('marketing.contact.sendMessage')
                  )}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center">
                  <Smartphone className="w-5 h-5 text-white" />
                </div>
                <span className="font-bold text-xl">ScanIt2Go</span>
              </div>
              <p className="text-gray-400">
                {t('marketing.footer.tagline')}
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">{t('marketing.footer.product')}</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#features" className="hover:text-white transition-colors">{t('marketing.footer.features')}</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">{t('marketing.footer.pricing')}</a></li>
                <li><a href="#solutions" className="hover:text-white transition-colors">{t('marketing.footer.solutions')}</a></li>
                <li><Link to="/admin" className="hover:text-white transition-colors">{t('marketing.footer.dashboard')}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">{t('marketing.footer.company')}</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/about" className="hover:text-white transition-colors">{t('marketing.footer.aboutUs')}</Link></li>
                <li><Link to="/faq" className="hover:text-white transition-colors">{t('marketing.footer.faq')}</Link></li>
                <li><a href="#contact" className="hover:text-white transition-colors">{t('marketing.footer.contact')}</a></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors">{t('marketing.footer.privacyPolicy')}</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">{t('marketing.footer.termsOfService')}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">{t('marketing.footer.contact')}</h4>
              <ul className="space-y-2 text-gray-400">
                <li>andy@scanit2go.com</li>
                <li>+44 7829 710 543</li>
                <li>www.scanit2go.com</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400">© 2026 ScanIt2Go. {t('marketing.footer.allRightsReserved')}</p>
            <div className="flex items-center gap-4 text-gray-400">
              <a href="#" className="hover:text-white transition-colors">Twitter</a>
              <a href="#" className="hover:text-white transition-colors">LinkedIn</a>
              <a href="#" className="hover:text-white transition-colors">Instagram</a>
            </div>
          </div>
        </div>
      </footer>
      
      {/* AI Chatbot */}
      <MarketingChatbot />
    </div>
  );
};

export default HomePage;
